import { Editor, Extension, Extensions } from "@tiptap/core";
import Image from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import TextAlign from "@tiptap/extension-text-align";
import {
  BackgroundColor,
  Color,
  TextStyle,
} from "@tiptap/extension-text-style";
import StarterKit from "@tiptap/starter-kit";

/** One indent step, matching what TinyMCE wrote (padding-left: 40px). */
const INDENT_PX = 40;
const MAX_INDENT = 8;
const INDENTABLE = ["paragraph", "heading"];

/**
 * Paragraph and heading indent, stored as inline padding-left so it survives
 * in emails and keeps the indents in HTML written by the old editor.
 */
const Indent = Extension.create({
  name: "indent",

  addGlobalAttributes() {
    return [
      {
        types: INDENTABLE,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (el) => {
              const px = parseInt(el.style.paddingLeft, 10);
              return px > 0
                ? Math.min(MAX_INDENT, Math.round(px / INDENT_PX))
                : 0;
            },
            renderHTML: (attrs) =>
              attrs.indent > 0
                ? { style: `padding-left: ${attrs.indent * INDENT_PX}px` }
                : {},
          },
        },
      },
    ];
  },
});

/**
 * Images are inline, as TinyMCE had them, and keep any inline style
 * (alignment, size) already in stored HTML. Pasted base64 images are
 * dropped; image files are uploaded instead (see RichTextEditor).
 */
const InlineImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      style: {
        default: null,
        parseHTML: (el) => el.getAttribute("style"),
        renderHTML: (attrs) => (attrs.style ? { style: attrs.style } : {}),
      },
    };
  },
}).configure({ inline: true, allowBase64: false });

export function buildExtensions(): Extensions {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4, 5, 6] },
      link: {
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
      },
    }),
    TextStyle,
    Color,
    BackgroundColor,
    TextAlign.configure({ types: INDENTABLE }),
    Indent,
    InlineImage,
    // No toolbar button, but stored or pasted tables are kept intact.
    TableKit.configure({ table: { resizable: false } }),
  ];
}

/**
 * Indent or outdent: nests or un-nests a list item, otherwise moves the
 * selected paragraphs and headings one step.
 */
export function changeIndent(editor: Editor, delta: 1 | -1) {
  if (editor.isActive("listItem")) {
    const chain = editor.chain().focus();
    (delta > 0
      ? chain.sinkListItem("listItem")
      : chain.liftListItem("listItem")
    ).run();
    return;
  }

  const { state, view } = editor;
  const { from, to } = state.selection;
  const tr = state.tr;
  state.doc.nodesBetween(from, to, (node, pos) => {
    if (!INDENTABLE.includes(node.type.name)) return true;
    const current = node.attrs.indent ?? 0;
    const next = Math.max(0, Math.min(MAX_INDENT, current + delta));
    if (next !== current) {
      tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
    }
    return false;
  });
  if (tr.docChanged) view.dispatch(tr);
  editor.commands.focus();
}
